import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MEMBERSHIP_ROLE_MAP, type MembershipRole } from '@goyone/shared';
import { api, setTenant } from '../../lib/api';
import { date } from '../../lib/format';
import { Logo } from '../../components/Logo';

interface Preview {
  email: string;
  /** الاسم كما سجّله المدير. */
  name: string;
  role: MembershipRole;
  tenantName: string;
  tenantSlug: string;
  expiresAt: string;
}

/**
 * قبول الدعوة.
 *
 * صفحة عامة بالضرورة: المدعو لا يملك حسابًا مفعَّلًا بعد فلا جلسة له. وهي
 * أول ما يراه من النظام، فتُظهر إلى أي شركة يدخل وبأي دور قبل أن يكتب شيئًا
 * — الرابط وحده لا يقول شيئًا عن مصدره.
 */
export function AcceptInvite() {
  const params = new URLSearchParams(window.location.search);
  const tenant = params.get('tenant') ?? '';
  const token = params.get('token') ?? '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const preview = useQuery({
    queryKey: ['invite', tenant, token],
    queryFn: () =>
      api<Preview>(
        `/public/invitations?tenant=${encodeURIComponent(tenant)}&token=${encodeURIComponent(token)}`,
        { skipAuth: true },
      ),
    enabled: Boolean(tenant && token),
    retry: false,
  });

  /* الاسمُ المسجَّل يُملأ في الحقل مرّةً — يراه المدعو فيُبقيه أو يصحّحه. */
  const [nameFilled, setNameFilled] = useState(false);
  useEffect(() => {
    if (!nameFilled && preview.data) {
      setName(preview.data.name ?? '');
      setNameFilled(true);
    }
  }, [preview.data, nameFilled]);

  const accept = useMutation({
    mutationFn: () =>
      api<{ tenantSlug: string; passwordSet: boolean }>('/public/invitations/accept', {
        method: 'POST',
        skipAuth: true,
        body: { tenant, token, password, ...(name ? { name } : {}) },
      }),
    onSuccess: (data) => {
      // النطاق يُثبَّت قبل التحويل، وإلا فتحت شاشة الدخول على شركة أخرى.
      setTenant(data.tenantSlug);
      setDone(true);
    },
  });

  if (!tenant || !token) {
    return (
      <Shell>
        <h1 className="auth-title">رابط غير مكتمل</h1>
        <p className="mute">
          هذا الرابط ناقص. افتح الرابط كما وصلك كاملًا، أو اطلب من مسؤول الشركة إعادة إرساله.
        </p>
      </Shell>
    );
  }

  if (preview.isLoading) {
    return (
      <Shell>
        <p className="mute">جارٍ التحقق من الدعوة</p>
      </Shell>
    );
  }

  if (preview.isError) {
    return (
      <Shell>
        <h1 className="auth-title">تعذّر قبول الدعوة</h1>
        <div className="alert alert-danger">
          {preview.error instanceof Error ? preview.error.message : 'رابط الدعوة غير صالح.'}
        </div>
        <p className="tiny mute mt-4">
          الدعوة صالحة لسبعة أيام وتُستعمل مرة واحدة. اطلب من مسؤول الشركة إعادة إرسالها.
        </p>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <h1 className="auth-title">جاهز</h1>
        <p className="mute">
          فُعِّل حسابك في {preview.data!.tenantName}. سجّل الدخول ببريدك وكلمة المرور التي
          اخترتها.
        </p>
        <a className="btn btn-primary btn-block mt-4" href={`/login?tenant=${tenant}`}>
          إلى تسجيل الدخول
        </a>
      </Shell>
    );
  }

  const data = preview.data!;
  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && password !== confirm;
  const valid = password.length >= 10 && password === confirm;

  return (
    <Shell>
      <h1 className="auth-title">انضمام إلى {data.tenantName}</h1>
      <p className="mute">اختر كلمة مرور لتفعيل حسابك.</p>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">البريد</span>
          <span className="num" dir="ltr">
            {data.email}
          </span>
        </div>
        <div className="totals-row">
          <span className="mute">الدور</span>
          <span>{MEMBERSHIP_ROLE_MAP[data.role].label}</span>
        </div>
        <div className="totals-row">
          <span className="mute">صالحة حتى</span>
          <span className="num">{date(data.expiresAt)}</span>
        </div>
      </div>

      {accept.isError && (
        <div className="alert alert-danger mt-4">
          {accept.error instanceof Error ? accept.error.message : 'تعذّر القبول.'}
        </div>
      )}

      <div className="field mt-4">
        <label className="label" htmlFor="a-name">
          اسمك
        </label>
        <input
          id="a-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="الاسم الأول والأخير"
        />
        <span className="tiny mute">هذا الاسم كما سجّله مسؤول الشركة — عدّله إن لزم.</span>
      </div>

      <div className="field mt-3">
        <label className="label req" htmlFor="a-password">
          كلمة المرور
        </label>
        <input
          id="a-password"
          className="input"
          type="password"
          dir="ltr"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span className={`tiny ${tooShort ? 'neg' : 'mute'}`}>عشرة أحرف على الأقل.</span>
      </div>

      <div className="field mt-3">
        <label className="label req" htmlFor="a-confirm">
          تأكيد كلمة المرور
        </label>
        <input
          id="a-confirm"
          className="input"
          type="password"
          dir="ltr"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {mismatch && <span className="tiny neg">الكلمتان غير متطابقتين.</span>}
      </div>

      <button
        className="btn btn-primary btn-block mt-4"
        onClick={() => accept.mutate()}
        disabled={!valid || accept.isPending}
      >
        {accept.isPending ? 'جارٍ التفعيل' : 'تفعيل الحساب'}
      </button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="login-brand">
          <Logo size={34} />
          <span>GoyOne</span>
        </div>
        {children}
      </div>
    </div>
  );
}
